import { useEffect } from 'react'
import { useLocations } from '@/hooks/useLocations'

interface LocationSelectProps {
  continent: string
  country: string
  state: string
  city: string
  onContinentChange: (value: string) => void
  onCountryChange: (value: string) => void
  onStateChange: (value: string) => void
  onCityChange: (value: string) => void
  required?: boolean
  disabled?: boolean
}

export default function LocationSelect({
  continent,
  country,
  state,
  city,
  onContinentChange,
  onCountryChange,
  onStateChange,
  onCityChange,
  required = true,
  disabled = false,
}: LocationSelectProps) {
  const { continents, useCountries, useStates, useCities } = useLocations()
  const { countries, isLoading: isLoadingCountries } = useCountries(continent)
  const { states, isLoading: isLoadingStates } = useStates(continent, country)
  const { cities, isLoading: isLoadingCities } = useCities(continent, country, state)

  // Reset country when continent changes
  useEffect(() => {
    if (continent && country && countries.length > 0) {
      // Only validate if countries have loaded
      // Check if current country is valid for selected continent
      const isValidCountry = countries.includes(country)
      if (!isValidCountry) {
        onCountryChange('')
        onStateChange('')
        onCityChange('')
      }
    }
  }, [continent, countries])

  // Reset state when country changes
  useEffect(() => {
    if (country && state && states.length > 0) {
      // Only validate if states have loaded
      // Check if current state is valid for selected country
      const isValidState = states.includes(state)
      if (!isValidState) {
        onStateChange('')
        onCityChange('')
      }
    }
  }, [country, states])

  // Reset city when state changes
  useEffect(() => {
    if (state && city && cities.length > 0) {
      // Only validate if cities have loaded
      // Check if current city is valid for selected state
      const isValidCity = cities.includes(city)
      if (!isValidCity) {
        onCityChange('')
      }
    }
  }, [state, cities])

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Continent */}
        <div>
          <label className="label">Continent {required && <span className="text-red-500">*</span>}</label>
          <select
            className="select"
            value={continent}
            onChange={(e) => onContinentChange(e.target.value)}
            required={required}
            disabled={disabled}
          >
            <option value="">Select Continent</option>
            {continents.map((c: string) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* Country */}
        <div>
          <label className="label">Country {required && <span className="text-red-500">*</span>}</label>
          <select
            className="select"
            value={country}
            onChange={(e) => onCountryChange(e.target.value)}
            disabled={disabled || !continent || isLoadingCountries}
            required={required}
          >
            <option value="">Select Country</option>
            {countries.map((c: string) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {isLoadingCountries && <p className="text-sm text-gray-500 mt-1">Loading countries...</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* State/Province */}
        <div>
          <label className="label">State/Province {required && <span className="text-red-500">*</span>}</label>
          <select
            className="select"
            value={state}
            onChange={(e) => onStateChange(e.target.value)}
            disabled={disabled || !country || isLoadingStates}
            required={required}
          >
            <option value="">Select State</option>
            {states.map((s: string) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {isLoadingStates && <p className="text-sm text-gray-500 mt-1">Loading states...</p>}
        </div>

        {/* City */}
        <div>
          <label className="label">City {required && <span className="text-red-500">*</span>}</label>
          <select
            className="select"
            value={city}
            onChange={(e) => onCityChange(e.target.value)}
            disabled={disabled || !state || isLoadingCities}
            required={required}
          >
            <option value="">Select City</option>
            {cities.map((c: string) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {isLoadingCities && <p className="text-sm text-gray-500 mt-1">Loading cities...</p>}
        </div>
      </div>
    </div>
  )
}
